import gsap from 'gsap';
import remap from 'lib/remap';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef } from 'react';

import classnames from 'classnames';
import { includes, last } from 'lib/helpers';
import styled, { keyframes } from 'styled-components';
import colors from 'styles/colors';

import { FILTERS_MODAL } from 'components/Scroller/constants';
import CaretBase from 'components/svg/Caret';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { CAROUSEL_NEXT_BTN, CAROUSEL_PREV_BTN } from './constants';

import media from 'styles/media';

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	position: relative;
	overflow: hidden;

	${media.small`
    flex-direction: column;
    align-items: stretch;
  `}
`;

const animateInPrev = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-5px);
  } 100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const animateInNext = keyframes`
  0% {
    opacity: 0;
    transform: translateX(5px);
  } 100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const NextButton = styled.button`
	position: absolute;
	top: -5px;
	right: 0;
	height: 35px;
	width: 35px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	background-color: white;

	opacity: 0;
	animation: ${animateInNext} 0.25s 0.4s cubic-bezier(0, 0, 0, 0) forwards;

	&.hide {
		display: none;
	}

	&:disabled {
		pointer-events: none;
		& > svg {
			opacity: 0.2;
		}
	}

	&:hover {
		svg {
			stroke: ${colors.primary.ocean};
		}
	}
`;

const PrevButton = styled(NextButton)`
	top: -4px;
	left: 0;
	right: unset;
	animation: ${animateInPrev} 0.25s 0.3s cubic-bezier(0, 0, 0, 0) forwards;
	justify-content: flex-start;
`;

const Caret = styled(CaretBase)`
	height: 6px;
	width: 12px;
	transition: opacity 0.35s cubic-bezier(0, 0, 0, 0);
`;

export const DAY_CLASS = 'date-range-day';
export const CAROUSEL_ITEM_CLASS = 'date-carousel-item-class';

/**
 * Controlled Carousel component
 * used in SearchCard and CruiseDetailModal
 *
 * @param {number} currentIndex Shows child of same index
 * @param {func} setCurrentIndex Sets new index to display
 * @param {func} onArrowsMouseEnter
 * @param {node} children Elements to display
 */

function Carousel({ children, currentIndex, setCurrentIndex, onArrowsMouseEnter }) {
	const { isMobile } = useContext(CruiseSelectionContext);
	const calendarRef = useRef();
	const prevIndex = useRef(currentIndex);
	const carouselMax = children.length - 1;

	const atEnd = currentIndex >= carouselMax - 1;
	const atStart = currentIndex === 0;
	const noArrows = isMobile;

	const moveCarousel = useCallback(() => {
		const widthEl = calendarRef.current?.querySelector(`.${CAROUSEL_ITEM_CLASS}`);
		const monthCardWith = widthEl?.clientWidth;
		if (!monthCardWith) return;

		const els = calendarRef.current?.querySelectorAll(`.${CAROUSEL_ITEM_CLASS}`);

		if (isMobile) {
			gsap.killTweensOf(els);
			gsap.to(els, {
				x: 0,
				opacity: 1,
				stagger: 0.1,
				duration: 0.4,
				delay: 0.2,
				ease: 'cubic-bezier(0,0,0,0)',
			});
			return;
		}

		const x = monthCardWith * currentIndex * -1;
		const goingForward = currentIndex - prevIndex.current > 0;
		let staggerIndices = [];
		if (goingForward) {
			staggerIndices = [currentIndex - 2, currentIndex - 1, currentIndex, currentIndex + 1, currentIndex + 2];
		} else {
			staggerIndices = [currentIndex + 3, currentIndex + 2, currentIndex + 1, currentIndex];
		}
		const inputLowerBound = staggerIndices[0];
		const inputUpperBound = last(staggerIndices);

		els?.forEach((el, index) => {
			const isStaggered = includes(staggerIndices, index);
			let delay = undefined;
			if (isStaggered) {
				const remappedIndex = remap(index, inputLowerBound, inputUpperBound, 0, staggerIndices.length);
				delay = remappedIndex * 0.03;
				gsap.to(el, {
					x,
					opacity: 1,
					duration: 0.5,
					delay,
					ease: 'cubic-bezier(0,0,0,0)',
				});
			} else {
				gsap.set(el, {
					x,
					opacity: 1,
					delay: 0.5,
					ease: 'cubic-bezier(0,0,0,0)',
				});
			}
		});
	}, [currentIndex, isMobile]);

	const onResize = useCallback(() => {
		moveCarousel();
	}, [moveCarousel]);

	/**
	 * Add/Remove listeners
	 */
	useEffect(() => {
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize]);

	/**
	 * Move carousel on index change
	 */
	useEffect(() => {
		moveCarousel();
	}, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

	/**
	 * Scroll to current index on mobile/mobile desktop
	 */
	useEffect(() => {
		const scrollingEl = document.getElementById(FILTERS_MODAL);

		if (!isMobile) {
			gsap.killTweensOf(scrollingEl);
			gsap.set(scrollingEl, { scrollTop: 0 });
		}

		const carouselItemEls = calendarRef.current?.querySelectorAll(`.${CAROUSEL_ITEM_CLASS}`) || [];
		const currentItem = carouselItemEls[currentIndex];
		let scrollTop = 0;
		let scrollingElHeight = 0;
		if (currentItem && scrollingEl) {
			const { y } = currentItem.getBoundingClientRect();
			const { height } = scrollingEl.getBoundingClientRect();
			scrollTop = y - 80;
			scrollingElHeight = height;
		}

		if (scrollTop > scrollingElHeight) {
			gsap.killTweensOf(scrollingEl);
			gsap.to(scrollingEl, { scrollTop, duration: 0.5, delay: 0.2 });
		}
	}, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

	const goNext = () => {
		let nextIndex = (currentIndex + 1) % carouselMax;
		if (nextIndex === 0) return;
		prevIndex.current = currentIndex;
		setCurrentIndex(nextIndex);
	};

	const goPrev = () => {
		let nextIndex = (currentIndex - 1) % carouselMax;
		if (nextIndex < 0) return;
		prevIndex.current = currentIndex;
		setCurrentIndex(nextIndex);
	};

	const prevArrowClasses = classnames({
		hide: noArrows,
		[CAROUSEL_PREV_BTN]: true,
	});
	const nextArrowClasses = classnames({
		hide: noArrows,
		[CAROUSEL_NEXT_BTN]: true,
	});

	return (
		<Wrapper ref={calendarRef}>
			{children}
			<NextButton onClick={goNext} onMouseEnter={onArrowsMouseEnter} disabled={atEnd} className={nextArrowClasses}>
				<Caret right />
			</NextButton>
			<PrevButton onClick={goPrev} onMouseEnter={onArrowsMouseEnter} disabled={atStart} className={prevArrowClasses}>
				<Caret left />
			</PrevButton>
		</Wrapper>
	);
}

Carousel.propTypes = {
	currentIndex: PropTypes.number.isRequired,
	setCurrentIndex: PropTypes.func.isRequired,
	onArrowsMouseEnter: PropTypes.func,
	children: PropTypes.node.isRequired,
};

export default Carousel;
